<template>
  <div class="tw-min-h-[calc(100vh-60px)] tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-indigo-50 tw-w-full tw-overflow-x-hidden">
    <div class="tw-w-full tw-p-[10px]">
      <div class="tw-mb-4 tw-w-full tw-border-b tw-border-white/40 tw-bg-white/35 tw-backdrop-blur-md tw-shadow-sm tw-shadow-slate-900/5 tw-py-4">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
          <div class="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-4">
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-white"
              @click="backToOrders"
              v-tippy="{content: 'Back to Orders', placement: 'top-end' }"
              title="Back to Orders"
              aria-label="Back to Orders"
            >
              <font-awesome-icon icon="arrow-left" />
            </button>
            <h1 class="tw-text-2xl sm:tw-text-3xl tw-font-semibold tw-tracking-tight tw-text-slate-800 tw-flex tw-items-center">
              Order
              <template v-if="order">
                #<CopyButton
                  :value="String(order.id)"
                  mode="wrap-content"
                  title="Copy order ID"
                  aria-label="Copy order ID to clipboard"
                >
                  {{ order.id }}
                </CopyButton>
              </template>
              
            </h1>
            
            <template v-if="order">
              <span class="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-white/60 tw-backdrop-blur tw-px-2.5 tw-py-1 tw-text-xl tw-font-medium tw-text-slate-700">
                <template
                  v-if="!order.isExport && order.billOfLading"
                >
                  BL# <CopyButton
                    :value="order.billOfLading.billOfLadingId"
                    mode="wrap-content"
                    title="Copy B/L ID"
                    aria-label="Copy B/L ID to clipboard"
                  >
                    {{ order.billOfLading.billOfLadingId }}
                  </CopyButton>
                </template>

                <template
                  v-if="order.houseBillOfLading && order.houseBillOfLading != ''"
                >
                  HBL# <CopyButton
                    :value="order.houseBillOfLading"
                    mode="wrap-content"
                    title="Copy house B/L"
                    aria-label="Copy house B/L to clipboard"
                  >
                    {{ order.houseBillOfLading }}
                  </CopyButton>
                </template>

                <template
                  v-else-if="order.bookingNumber"
                >
                  BKG # <CopyButton
                    :value="String(order.bookingNumber)"
                    mode="wrap-content"
                    title="Copy booking number"
                    aria-label="Copy booking number to clipboard"
                  >
                    {{ order.bookingNumber }}
                  </CopyButton>
                </template>
              </span>

              <span class="tw-text-lg tw-font-bold">
                <template v-if="order.containers.length">
                  <span v-if="order.isExport" class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-1  tw-bg-amber-100 tw-text-amber-900">{{ order.containers.length }} Export{{ order.containers.length > 1 ? 's' : '' }}</span>
                  <span v-else class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-1 tw-bg-sky-100 tw-text-sky-900">{{ order.containers.length }} Import{{ order.containers.length > 1 ? 's' : '' }}</span>
                </template>
              </span>

              <span class="tw-text-lg">
                <template v-if="!isEditStatus">
                  <template
                    v-if="(
                      order.status != 'Complete' &&
                      orderCompletionStatus() == 'Complete'
                    )"
                  >
                    <font-awesome-icon icon="warning" class="tw-h-4 tw-w-4 tw-text-rose-900" v-tippy="'All trips for active (non-cancelled) containers are complete. You should complete the order.'" />
                  </template>
                  <span v-if="!order.status" class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-500/15 tw-px-2.5 tw-py-1 tw-font-medium tw-text-amber-800">Unknown</span>
                  <span v-else-if="order.status == 'Cancelled'" class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-rose-500/15 tw-px-2.5 tw-py-1 tw-font-medium tw-text-rose-800">Cancelled</span>
                  <span v-else-if="order.status == 'Complete'" class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-indigo-500/15 tw-px-2.5 tw-py-1 tw-font-medium tw-text-indigo-800">Complete</span>
                  <span v-else-if="order.status == 'Not Available'" class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-rose-500/15 tw-px-2.5 tw-py-1 tw-font-medium tw-text-rose-800">Not Available</span>
                  <span v-else-if="order.status === 'In Progress'" class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-emerald-500/15 tw-px-2.5 tw-py-1 tw-font-medium tw-text-emerald-800">{{ order.status }}</span>
                  <span v-else class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-500/15 tw-px-2.5 tw-py-1 tw-font-medium tw-text-slate-800">{{ order.status }}</span>
                  <font-awesome-icon icon="pen-to-square" @click="isEditStatus = true" class="tw-cursor-pointer" />
                  
                </template>
                <template v-else>
                  <select
                    class="tw-h-10 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-text-slate-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/20"
                    v-model="orderStatus"
                    @change="selectOrderStatus"
                  >
                    <option value="Not Available">Not Available</option>
                    <option>Available</option>
                    <option>In Progress</option>
                    <option v-if="orderCompletionStatus() == 'Complete'">
                      Complete
                    </option>
                    <option
                      v-else
                      disabled
                      title="Please complete all trips before completing the order"
                    >
                      Complete
                    </option>
                    <option>Cancelled</option>
                  </select>
                  <font-awesome-icon icon="xmark" @click="isEditStatus = false" class="tw-cursor-pointer" />
                </template>
              </span>
              
            </template>

            <span v-else class="tw-text-sm tw-text-slate-600">Review details, containers, notes, and logs.</span>
          </div>
          <div v-if="order">
            <div class="tw-flex tw-flex-wrap tw-gap-2">

              <span
                v-if="order.createdAt"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-white/50 tw-bg-white/40 tw-backdrop-blur tw-px-2.5 tw-py-1 tw-text-sm tw-text-slate-600"
              >
                Created: {{ order.createdAt }}
              </span>
              
              <div
                v-if="order.linkedMasterOrderNumber"
                class="tw-text-sm tw-font-semibold tw-text-slate-700"
              >
                Linked Order # {{ order.linkedMasterOrderNumber }}
              </div>
              <button
                v-if="order.status == 'Complete'"
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
                @click="goToBillingPageForThisOrder(order.id)"
              >
                Billing Page
              </button>
              <a
              class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-white"
              :href="printCoverURL(order.id)"
              target="_blank"
              >
                Cover Sheet <font-awesome-icon icon="print" class="tw-h-4 tw-w-4 tw-ml-2" />
              </a>
              
              <button
                v-if="order"
                type="button"
                :class="order?.notes?.length > 0
                  ? 'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-emerald-300 tw-bg-emerald-50/80 tw-backdrop-blur tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-emerald-700 tw-shadow-sm tw-shadow-emerald-900/5 hover:tw-bg-emerald-100/80'
                  : 'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-white'"
                @click="openNotesModal"
              >
                <font-awesome-icon icon="sticky-note" />
                <span>Notes</span>
                <span
                  v-if="order?.notes?.length > 0"
                  class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-emerald-700 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-white"
                  >
                        {{ order.notes.length }}
                </span>
              </button>

              <button
                v-if="order"
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-white"
                @click="openLogsModal"
              >
                <font-awesome-icon icon="eye" />
                <span>Logs</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="$apollo.queries.order.loading" class="tw-flex tw-items-center tw-justify-center tw-py-16">
        <font-awesome-icon icon="circle-notch" spin class="tw-h-10 tw-w-10 tw-text-slate-400" />
      </div>

      <div v-else-if="order" class="tw-text-left">
        <Modal
          v-model:isOpen="isContainerModalOpen"
          :modalId="`container-modal-${order.id}-${currentContainerId}`"
          maxWidth="7xl"
        >
          <template #title>
            Container
              <template v-if="currentContainer?.containerId === 'EXPUNDEF'">(no number)</template>
              <CopyButton v-else
                :value="currentContainer?.containerId || ''"
                mode="wrap-content"
                title="Copy container number"
                aria-label="Copy container number to clipboard"
              >
                {{ currentContainer?.containerId || '' }}
              </CopyButton>-{{ String(containersWithTrips.findIndex(c => c.id === currentContainerId) + 1).padStart(2,'0') }}
          </template>
          <template #subtitle>
            Order #<CopyButton
              :value="order.id"
              mode="wrap-content"
              title="Copy order ID"
              aria-label="Copy order ID to clipboard"
            >
              {{ order.id }}
            </CopyButton>
          </template>
          <template #content>
            <OrderContainerDetails
                :chassis="chassis"
                :drivers="drivers"
                :order="order"
                :editable="editable"
                :containerId="currentContainerId"
              >
              </OrderContainerDetails>
          </template>
        </Modal>
        
        <!-- todo: (one day) remove @addedNote="updateNotes" and replace with an apollo.getClient.writeFragment() to update apollo query cache in place; else order gets refreshed unnecessarily -->
        <AddNotesModal
          :open="isNotesModalOpen"
          title="Notes"
          :subtitle="order ? `Order #${order.id}` : undefined"
          :order="order"
          @close="closeNotesModal"
          @addedNote="updateNotes"
        />

        <OrderLogsModal
          v-model:isOpen="isLogsModalOpen"
          :order="order"
          @close="closeLogsModal"
        />

        
        <div class="tw-mt-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
          <!-- <uk-divider></uk-divider> -->
          <TerminalAddressCard
            class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-900/5 tw-p-4"
            :isExportOrder="isExportOrder"
            :order="order"
            @updateOrder="updateOrder"
          ></TerminalAddressCard>
          <ReceiverAddressCard
            @refreshOrder="emitRefreshOrders"
            :order="order"
            @updateOrder="updateOrder"
          >
          </ReceiverAddressCard>
          <BillToAddressCard
            :billingContact="order.billingContact"
            :orderId="order.id"
            :billTo="order.billTo"
            @updateOrder="updateOrder"
          ></BillToAddressCard>
        </div>

        <div class="tw-mt-4 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50 tw-p-3 sm:tw-p-4">
          <div class="tw-overflow-x-auto">
            <table
              class="tw-min-w-full tw-text-left tw-text-sm tw-text-slate-700"
            >
              <thead>
                <tr>
                  <th v-if="isOceanOrder">Ship Line</th>
                  <th v-if="isOceanOrder">Vessel Name</th>
                  <th v-if="isOceanOrder">
                    {{ !isExportOrder ? 'Vessel ETA' : 'Vessel Cutoff' }}
                  </th>
                  <th v-if="isOceanOrder">Vessel Tracking</th>
                  <th>Reference #</th>
                  <th>Billing Customer Ref #</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-if="!isEditingVesselInformation && isOceanOrder">
                    {{ order.shipLine }}
                  </td>
                  <td v-else-if="isOceanOrder">
                    <FilterableDropdown
                      taggable
                      push-tag
                      v-model="shipLine"
                      :options="steamshipLines"
                      :components="{ OpenIndicator: null, Deselect: null }"
                    ></FilterableDropdown>
                  </td>
                  <td
                    v-else-if="
                      isExportOrder &&
                      isEditingVesselInformation &&
                      isOceanOrder
                    "
                  >
                    <InputField
                      id="editBookingNumber"
                      v-model="newBookingNumber"
                    ></InputField>
                  </td>
                  <td v-if="!isEditingVesselInformation && isOceanOrder">
                    {{ order.vesselName }}
                  </td>
                  <td v-else-if="isOceanOrder" class="tw-flex tw-items-center tw-gap-2 tw-nowrap">
                    <FilterableDropdown
                      taggable
                      push-tag
                      v-model="vesselName"
                      :options="filteredVessels"
                      :components="{ OpenIndicator: null, Deselect: null }"
                    ></FilterableDropdown>

                    <button
                      type="button"
                      class="tw-h-10 tw-shrink-0 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                      @click="openAddVesselModal"
                    >Add</button>
                    <EditVessel
                      v-model:isOpen="showAddVesselModal"
                      @close="closeAddVesselModal"
                      @saved="onAddVesselSaved"
                    />
                  </td>
                  <td v-if="!isEditingVesselInformation && isOceanOrder">
                    {{ formatVesselEtaDate(order.vesselEtaString) }}
                  </td>
                  <td v-else-if="isOceanOrder">
                    <DatePicker
                      class="uk-form-label"
                      :input-class="'uk-input uk-width-medium'"
                      :clearable="true"
                      format="MM/DD/YYYY"
                      @change="changedETA"
                      v-model="vesselEta"
                      show-days-shortcuts
                      controls-placement="popup"
                    ></DatePicker>
                    <!-- <InputField id="vesselETA" v-model="vesselEta"></InputField> -->
                  </td>
                  <td v-if="isOceanOrder">
                    <button
                      v-if="order.vessel && order.vessel.mssi"
                      type="button"
                      class="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white/60 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow-sm hover:tw-bg-white"
                      @click="isVesselTrackingOpen = !isVesselTrackingOpen"
                    >
                      <font-awesome-icon icon="ship" class="tw-h-3.5 tw-w-3.5" />
                      {{ isVesselTrackingOpen ? 'Hide Tracking' : 'View Tracking' }}
                    </button>
                    <span v-else class="tw-text-xs tw-text-slate-400">No vessel data</span>
                  </td>
                  <td>
                    <div v-if="!order.referenceNumber">
                      <div v-if="isEditingReferenceNumber">
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                          <input
                            type="text"
                            v-model="editingReferenceNumber"
                            class="tw-w-64 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                            placeholder="Reference #"
                          />
                          <Button
                            @click="saveReferenceNumber"
                            class="tw-h-9 tw-w-9 tw-p-0"
                            icon="check"
                            title="Save reference number"
                            aria-label="Save reference number"
                          />
                          <Button
                            :isGray="true"
                            @click="cancelReferenceNumberEdit"
                            class="tw-h-9 tw-w-9 tw-p-0"
                            icon="xmark"
                            title="Cancel reference number edit"
                            aria-label="Cancel reference number edit"
                          />
                        </div>
                      </div>
                      <Button v-else @click="editReferenceNumber"
                        >Add Ref Number</Button
                      >
                    </div>
                    <div v-else-if="!isEditingVesselInformation">
                      {{ order.referenceNumber }}
                    </div>
                    <InputField
                      v-else
                      id="editReferenceNumber"
                      v-model="newReferenceNumber"
                    ></InputField>
                  </td>
                  <td>
                    <div v-if="!isEditingVesselInformation">
                      {{ order.billingReferenceNumber }}
                    </div>
                    <InputField
                      v-else
                      id="editReferenceNumber"
                      v-model="newBillingReferenceNumber"
                    ></InputField>
                  </td>
                  <td>
                    <button
                      v-if="!isEditingVesselInformation"
                      type="button"
                      class="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-slate-900/5 hover:tw-text-slate-900"
                      @click="editVesselInformation"
                      aria-label="Edit vessel information"
                      title="Edit vessel information"
                    >
                      <font-awesome-icon icon="pen-to-square" class="tw-h-4 tw-w-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="isEditingVesselInformation" class="tw-mt-3 tw-flex tw-justify-end tw-gap-2">
            <Button
              :isGreen="true"
              @click="saveVesselInformation"
              icon="check"
              aria-label="Save vessel information"
              title="Save vessel information"
            >Save</Button>
            <Button
              :isRed="true"
              :isSecondary="true"
              @click="cancelEditVesselInformation"
              aria-label="Cancel vessel information edit"
              title="Cancel vessel information edit"
            >Cancel</Button>
          </div>
        </div>

        <div class="tw-mt-4 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50">
          <OrderContainersTable
            :order="order"
            @showContainer="showContainer"
            :containersWithTrips="containersWithTrips"
            @refreshOrders="emitRefreshOrders"
            :chassis="chassis"
            :drivers="drivers"
            :isLoadingContainersQuery="!!$apollo?.queries?.orderContainers?.loading"
            @updateContainers="updateContainers"
            @showContainerDetails="showContainerDetails"
          ></OrderContainersTable>
        </div>
        
        <div class="tw-mt-4" v-if="company.settingsOrderShowEmergencyInformation">
          <div class="uk-flex uk-margin-small-bottom">
            <h3 class="uk-margin-remove-bottom uk-width-1-6">
              Emergency Information
            </h3>
            <a
              class="uk-icon-link uk-align-left uk-margin-remove"
              type="button"
              @click="toggleIsEditingEmergencyInformation"
            >
              <span uk-icon="icon:  file-edit; ratio:1.2"></span>
            </a>
          </div>
          <div v-if="!isEditingEmergencyInformation">
            <p>
              <strong>Phone Number:</strong>
              {{ order.emergencyResponsePhoneNumber }}
            </p>
          </div>
          <div v-else>
            <InputField
              class="uk-width-1-6"
              id="EmergencyPhoneNumber"
              label="Phone Number"
              v-model="emergencyInformation.phoneNumber"
            ></InputField>
          </div>
          <div v-if="isEditingEmergencyInformation" class="tw-mt-3 tw-flex tw-justify-end tw-gap-2">
            <Button
              :isDefault="true"
              class="uk-button-primary tw-h-9 tw-w-9 tw-p-0"
              @click="saveEmergencyInformation"
              icon="check"
              title="Save emergency information"
              aria-label="Save emergency information"
            />
            <Button
              @click="cancelEditEmergencyInformation"
              :isDefault="true"
              class="uk-button-secondary tw-h-9 tw-w-9 tw-p-0"
              :isGray="true"
              icon="xmark"
              title="Cancel emergency information edit"
              aria-label="Cancel emergency information edit"
            />
          </div>
        </div>

      </div>
    </div>
    <Modal v-model:isOpen="isVesselTrackingOpen" :modalId="`vessel-tracking-${order.id}`" maxWidth="4xl" v-if="order && order.vessel && order.vessel.mssi">
      <template #title>Vessel Tracking: {{ order.vesselName }} ({{ order.vessel.mssi }})</template>
      <template #subtitle></template>  
      <template #content>
        <VesselFinderEmbed :mssi="order.vessel.mssi" height="100%" :isShowTitle="false" isFitParentDimensions/>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, ComponentOptions } from 'vue';

import moment from 'moment';

import EditReferenceNumber from '@/gql/orders/mutations/EditReferenceNumber.gql';

import EditEmergencyInformation from '@/gql/orders/mutations/EditEmergencyInformation.gql';

import EditOrderStatus from '@/gql/orders/mutations/EditOrderStatus.gql';
import CompanyQuery from '@/gql/company/Company.gql';
import OrderQuery from '@/gql/orders/queries/Order.gql';
import VesselsQuery from '@/gql/orders/Vessels.gql';
import DriversQuery from '@/gql/drivers/Drivers.gql';
import ChassisQuery from '@/gql/orders/Chassis.gql';

import DatePicker from '../core/DateTime/DatePicker.vue';
import OrderContainersQuery from '@/gql/orders/queries/OrderContainers.gql';

import EditVessel from '@/components/Vessel/EditVessel.vue';

// Trips
import FilterableDropdown from '../core/FilterableDropdown.vue';
import EditShiplineInfo from '../../gql/orders/mutations/EditShiplineInfo.gql';
import { markTrackingContainersStale } from '@/utils/trackingContainersCache';
import { SteamShipLines, SteamShipLinesMap } from '../../utils/constants';
import InputField from '../core/Input/InputField.vue';

import Button from '../core/Button.vue';
import BillToAddressCard from './AddressCards/BillToAddressCard.vue';
import TerminalAddressCard from './AddressCards/TerminalAddressCard.vue';
import ReceiverAddressCard from './AddressCards/ReceiverAddressCard.vue';

// logs
import OrderContainersTable from './OrderDetail/OrderContainersTable.vue';
import OrderContainerDetails from './OrderDetail/OrderContainerDetails.vue';

import CopyButton from '@/components/core/CopyButton.vue';
import AddNotesModal from '@/components/core/AddNotesModal.vue';
import OrderLogsModal from './OrderDetail/OrderLogsModal.vue';
import VesselFinderEmbed from '@/components/ContainerTracker/Vessel/VesselFinderEmbed.vue';

// Contacts
import UpdateAcceptedStateMutation from '../../gql/orders/mutations/UpdateAcceptedState.gql';
import ReceivingCustomersQuery from '../../gql/customers/ReceivingCustomers.gql';
import { addressMixin, Address } from '../../utils/address';

import { Container, TRIP_STATUS } from '../../types';
import { dateFormatMixin } from '../../utils/date';
import Modal from '@/components/core/Modal/Modal.vue';


interface OrderDetailsComponent extends ComponentOptions {
    fullAddress(address: Address): string;
    addressLine2(address: Address): string;
    
    formatDate(dateStr: string): string;
}

export default defineComponent({
  props: {
    orderId: {
      type: Number,
      required: true,
    },
    activeContainersWithoutOrder: Array,
    show: Boolean,
  },
  mixins: [addressMixin, dateFormatMixin],
  components: {
    TerminalAddressCard,
    FilterableDropdown,
    Button,
    InputField,
    OrderContainersTable,
    BillToAddressCard,
    ReceiverAddressCard,
    CopyButton,
    AddNotesModal,
    OrderLogsModal,
    DatePicker,
    OrderContainerDetails,
    EditVessel,
    VesselFinderEmbed,
    Modal,
  },
  data(): any {
    return {
      drivers: [],
      chassis: [],
      vessels: [],
      company: {},
      loading: false,
      isNotesModalOpen: false,
      isLogsModalOpen: false,
      isEditingReferenceNumber: false,
      editingReferenceNumber: undefined,
      originalReferenceNumber: undefined,
      orderStatus: undefined,
      steamshipLines: [...SteamShipLines],

      // Vessel information
      isEditingVesselInformation: false,
      vesselName: undefined,
      vesselEta: undefined,
      shipLine: undefined,
      newReferenceNumber: undefined,
      newBookingNumber: undefined,
      newBillingReferenceNumber: undefined,

      // Contacts
      originCustomerNameQuery: '',
      destinationCustomerNameQuery: '',

      // Emergency info
      isEditingEmergencyInformation: false,
      emergencyInformation: {
        phoneNumber: undefined,
      },
      currentContainerId: -1,
      isContainerModalOpen: false,
      isEditStatus: false,

      // Vessel Add
      showAddVesselModal: false,

      // Vessel Tracking
      isVesselTrackingOpen: false,
    };
  },
  created() {
    const vm = (this as any);

    vm.TRIP_STATUS = TRIP_STATUS;
  },
  computed: {
    isOceanOrder(): boolean {
      const vm = (this as any);

      return (
        vm.order.orderType === 'Import' || vm.order.orderType === 'Export'
      );
    },
    showExportOptions(): boolean {
      const vm = this as any;
      return (
        vm.order.orderType === 'Import' || vm.order.orderType === 'Export'
      );
    },
    orderTripCountText(): string {
      const vm = this as any;
      if (!vm.order || !vm.order.containers) return '';
      const count = vm.order.containers.reduce(
        (acc, container) => acc + container.trips.length,
        0
      );
      return `${count} Trip${count > 1 ? 's' : ''}`;
    },
    filteredVessels(): any[] {
      const vm = this as any;
      // make this a O(N) function not N^2
      // de-dup vessels, skip blank names
      if (!vm.vessels) return [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const v of vm.vessels) {
        if (v.name && !seen.has(v.name)) {
          seen.add(v.name);
          out.push(v.name);
        }
      }
      return out;
    },
    acceptedState(): string {
      const vm = (this as any);

      if (!vm.order) return '';
      return vm.order.acceptedState || 'Not Accepted';
    },
    isExportOrder(): boolean {
      const vm = (this as any);

      if (!vm.order) {
        return false;
      }
      return vm.order.isExport;
    },
  containersWithTrips(): any[] {
    const vm = (this as any);

    if (!vm.order || !vm.orderContainers) {
        return [];
    }

    const trips = vm.order.trips || [];
    
    const tripsByContainerId = trips.reduce((acc, trip) => {
        const id = trip.container.id;
        if (!acc[id]) {
            acc[id] = [];
        }
        acc[id].push(trip);
        return acc;
    }, {});


    const tripContainers = vm.orderContainers.map(originalContainer => {
        
        const containerCopy = { ...originalContainer }; 
        
        const containerTrips = (tripsByContainerId[containerCopy.id] || []).sort((a, b) => {
             return Number(a.legId) < Number(b.legId) ? -1 : 1;
        });
        
        containerCopy.trips = containerTrips;

        return containerCopy;
    });

    if (vm.order.isExport) {
        return tripContainers.sort((a, b) => a.id.localeCompare(b.id));
    } else {
        return tripContainers.sort((a, b) =>
            a.containerId.localeCompare(b.containerId),
        );
    }
  },
    currentContainer(): any {
      const vm = (this as any);
      return (vm.containersWithTrips || []).find(c => c.id === vm.currentContainerId) || {};
    }
  },
  methods: {
    invalidateOrdersListCache() {
      const vm = (this as any);
      const client = vm?.$apollo?.provider?.defaultClient;
      if (!client?.cache) return;

      // Evict all cached variants of ROOT_QUERY.orders
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'orders' });
      client.cache.gc();
    },
    parseVesselEtaToDate(eta: any): Date | undefined {
      if (!eta) return undefined;
      if (eta instanceof Date) return eta;
      if (typeof eta !== 'string') return undefined;

      const str = eta.trim();
      if (!str) return undefined;

      const strictFormats = [
        moment.ISO_8601,
        'MM/DD/YYYY',
        'MM/DD/YYYY HH:mm',
        'MM/DD/YYYY HH:mm:ss',
        'YYYY-MM-DD',
        'YYYY-MM-DD HH:mm',
        'YYYY-MM-DD HH:mm:ss',
      ];

      let m = moment(str, strictFormats, true);
      if (!m.isValid()) {
        m = moment(str);
      }
      return m.isValid() ? m.toDate() : undefined;
    },
    formatVesselEtaDate(eta: Date | undefined)  {
      if (!eta) return '';
      return moment(eta).format('MM/DD/YYYY');
    },
    openNotesModal() {
      (this as any).isNotesModalOpen = true;
    },
    closeNotesModal() {
      (this as any).isNotesModalOpen = false;
    },
    openLogsModal() {
      (this as any).isLogsModalOpen = true;
    },
    closeLogsModal() {
      (this as any).isLogsModalOpen = false;
    },
    showContainerDetails(containerId) {
      const vm = (this as any);
      vm.currentContainerId = containerId;
      vm.isContainerModalOpen = true;
    },
    backToOrders() {
      const vm = (this as any);

      const desRoute = '/orders';
      vm.$router.push(desRoute);
    },
    updateContainers() {
      const vm = (this as any);

      vm.$apollo.queries.orderContainers.refetch();
    },
    changedETA(val: any) {
      const vm = (this as any);
      vm.vesselEta = val;
    },
    orderBillingURL(orderId): any {
      return `/billing/${orderId}`;
    },
    goToBillingPageForThisOrder(orderId: number) {
      const vm = (this as any);

      vm.$router.push(this.orderBillingURL(orderId));
    },
    showContainer(container: Container) {
      const vm = (this as any);
      // this.hideModal();
      // this.$router.push();

      vm.$emit('showContainer', vm.containerDetailURL(container.id));
    },
    saveVesselInformation() {
      const vm = (this as any);

      console.log('Shipline:', vm.shipLine);
      console.log('Vessel ETA:', vm.vesselEta);
      console.log('Vessel Name:', vm.vesselName);

      if (
        vm.shipLine === '' ||
        vm.vesselName === '' ||
        vm.newReferenceNumber === ''
      ) {
        return;
      }

      const etaValue =
        vm.vesselEta == null || vm.vesselEta === ''
          ? ''
          : vm.vesselEta && typeof vm.vesselEta === 'object' && vm.vesselEta instanceof Date
            ? moment(vm.vesselEta).format('MM/DD/YYYY')
            : vm.vesselEta;

      vm.$apollo
        .mutate({
          mutation: EditShiplineInfo,
          variables: {
            orderId: Number(vm.order.id),
            shipLineInfo: {
              line:
                typeof vm.shipLine === 'string'
                  ? vm.shipLine.split(' ')[0]
                  : vm.shipLine,
              vessel: vm.vesselName,
              eta: etaValue,
            },
            referenceNumber: vm.newReferenceNumber,
            bookingNumber: vm.newBookingNumber,
            billingReferenceNumber: vm.newBillingReferenceNumber,
          },
        })
        .then((data) => {
          vm.isEditingVesselInformation = false;
          vm.vesselName = undefined;
          vm.vesselEta = undefined;
          vm.shipLine = undefined;
          vm.newReferenceNumber = undefined;
          vm.newBookingNumber = undefined;
          vm.newBillingReferenceNumber = undefined;
          vm.$apollo.queries.order.refetch();
          markTrackingContainersStale();
        })
        .catch((err) => {
          vm.isEditingVesselInformation = false;
          console.error('Could not update ship line information');
          console.error(err);
        });
    },

    editVesselInformation() {
      const vm = (this as any);

      vm.vesselName = vm.order.vesselName;

      vm.vesselEta = vm.parseVesselEtaToDate(vm.order.vesselEtaString);

      const rawShipLine = typeof vm.order.shipLine === 'string' ? vm.order.shipLine.trim() : vm.order.shipLine;
      const mappedShipLine =
        (rawShipLine && (SteamShipLinesMap as any)[rawShipLine]) || rawShipLine;

      if (mappedShipLine && typeof mappedShipLine === 'string') {
        const hasOption = (vm.steamshipLines || []).includes(mappedShipLine);
        if (!hasOption) {
          vm.steamshipLines = [mappedShipLine, ...(vm.steamshipLines || [])];
        }
      }

      vm.shipLine = mappedShipLine;
      vm.newReferenceNumber = vm.order.referenceNumber;
      vm.newBookingNumber = vm.order.bookingNumber;
      vm.newBillingReferenceNumber = vm.order.billingReferenceNumber;
      vm.isEditingVesselInformation = true;
    },
    cancelEditVesselInformation() {
      const vm = (this as any);

      vm.isEditingVesselInformation = false;
    },

    openAddVesselModal() {
      const vm = this as any;
      vm.showAddVesselModal = true;
    },
    closeAddVesselModal() {
      (this as any).showAddVesselModal = false;
    },
    onAddVesselSaved(vessel: any) {
      const vm = this as any;
      vm.vesselName = vessel?.name || vm.vesselName;
      vm.showAddVesselModal = false;
    },

    toggleIsEditingEmergencyInformation() {
      const vm = (this as any);

      vm.emergencyInformation.phoneNumber = vm.order.emergencyResponsePhoneNumber;
      vm.isEditingEmergencyInformation = !vm.isEditingEmergencyInformation;
    },
    cancelEditEmergencyInformation() {
      const vm = (this as any);

      vm.isEditingEmergencyInformation = false;
    },

    saveEmergencyInformation() {
      const vm = (this as any);

      if (
        vm.emergencyInformation.phoneNumber === undefined ||
        vm.emergencyInformation.phoneNumber === ''
      ) {
        return;
      }

      vm.$apollo
        .mutate({
          mutation: EditEmergencyInformation,
          variables: {
            orderId: Number(vm.order.id),
            emergencyResponsePhoneNumber: vm.emergencyInformation.phoneNumber,
          },
        })
        .then((data) => {
          vm.isEditingEmergencyInformation = false;
          vm.emergencyInformation.phoneNumber = undefined;
        })
        .catch((err) => {
          vm.isEditingEmergencyInformation = false;
          console.error('Could not update emergency information');
          console.error(err);
        });
    },

    orderCompletionStatus() {
      const vm = (this as any);

      // Cancelled containers are excluded from trip completion requirements
      const containersRequiringTrips = vm.containersWithTrips.filter(
        (container) => !container.isCancelled,
      );

      if (containersRequiringTrips.length === 0) {
        return TRIP_STATUS.COMPLETE;
      }

      const containerCompletions = containersRequiringTrips.map((x) =>
        this.tripCompletionText(x.trips),
      );

      if (containerCompletions.includes('In Progress')) {
        return TRIP_STATUS.IN_PROGRESS;
      } else if (
        !containerCompletions.includes('In Progress') &&
        !containerCompletions.includes('Complete')
      ) {
        return TRIP_STATUS.NOT_STARTED;
      } else if (containerCompletions.includes(TRIP_STATUS.NOT_STARTED)) {
        return TRIP_STATUS.IN_PROGRESS;
      } else {
        return TRIP_STATUS.COMPLETE;
      }
    },
    emitRefreshOrders() {
      console.info('hit refresh order');
      // this.$apollo.queries.order.refresh();
    },
    updateOrder() {
      const vm = (this as any);

      vm.$apollo.queries.order.refetch();
    },
    updateNotes(notes) {
      const vm = (this as any);

      vm.$apollo.queries.order.refetch();
    },
    updateAcceptedState(newState: string) {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: UpdateAcceptedStateMutation,
          variables: {
            orderId: vm.order.id,
            acceptedState: newState,
          },
        })
        .then((data) => {
          // Avoid mutating Apollo objects directly; refetch the order
          vm.$apollo.queries.order.refetch();
        })
        .catch((err) => {
          console.error(err);
        });
    },
    tripCompletionText(trips: any[]) {
      const nTrips = trips.length;
      const completedTrips = trips.filter((x) => x.status === 'Complete');
      const inProgressTrips = trips.filter((x) => x.status === 'In Progress');

      if (inProgressTrips.length === 0 && completedTrips.length === 0) {
        return TRIP_STATUS.NOT_STARTED;
      } else if (completedTrips.length === nTrips) {
        return TRIP_STATUS.COMPLETE;
      } else {
        return TRIP_STATUS.IN_PROGRESS;
      }
    },
    closeModal() {
    },
    containerDetailURL(containerIdx): any {
      return `/tracking/${containerIdx}`;
    },
    printCoverURL() {
      return `/print/order/cover/${(this as any).order.id}`;
    },
    cancelContainerNumberEdit() {
      const vm = (this as any);

      vm.isEditingContainerNumber = -1;
      vm.editingContainerNumber = undefined;
    },
    updateOrderStatus() {
      const vm = (this as any);

      vm.updateOrderStatusMutation(vm.orderStatus);
    },
    updateOrderStatusMutation(status: string) {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: EditOrderStatus,
          variables: {
            orderId: Number(vm.order.id),
            status,
          },
        })
        .then(() => {
          vm.invalidateOrdersListCache();
          vm.$apollo.queries.order.refetch();
        });
    },
    selectOrderStatus(event: string) {
      const vm = (this as any);

      console.log('status is');
      console.log(vm.orderStatus);
      this.updateOrderStatusMutation(vm.orderStatus);
      vm.isEditStatus = false;
    },
    editReferenceNumber() {
      const vm = (this as any);
      vm.originalReferenceNumber = vm.order?.referenceNumber;
      vm.editingReferenceNumber = vm.order?.referenceNumber || '';
      vm.isEditingReferenceNumber = true;
    },
    cancelReferenceNumberEdit() {
      const vm = (this as any);
      vm.isEditingReferenceNumber = false;
      vm.editingReferenceNumber = vm.originalReferenceNumber;
      vm.originalReferenceNumber = undefined;
    },
    saveReferenceNumber() {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: EditReferenceNumber,
          variables: {
            orderId: Number(vm.order.id),
            referenceNumber: vm.editingReferenceNumber,
          },
        })
        .then((data) => {
          vm.isEditingReferenceNumber = false;
          vm.editingReferenceNumber = undefined;
          vm.originalReferenceNumber = undefined;
          vm.$apollo.queries.order.refetch();
        })
        .catch((err) => {
          vm.isEditingReferenceNumber = false;
          vm.editingReferenceNumber = undefined;
          vm.originalReferenceNumber = undefined;
          console.error('Could not update reference number');
          console.error(err);
        });
    },
    onDestinationCustomerSearch(customerName) {
      const vm = (this as any);

      vm.destinationCustomerNameQuery = customerName;
    },
  },
  ...{
    apollo: {
      order: {
        fetchPolicy: 'network-only',
        query: OrderQuery,
        deep: true,
        variables() {
          return {
            id: (this as any).orderId,
          };
        },
        skip() {
          return (this as any).orderId === -1;
        },
        result(result) {
          const vm = (this as any);
          vm.orderStatus = vm.order ? vm.order.status : undefined;
        },
        error(error) {
          console.error("We've got an error!", error);
        },
      },
      orderContainers: {
        query: OrderContainersQuery,
        variables() {
          return {
            id: (this as any).orderId,
          };
        },
        skip() {
          return (this as any).orderId === -1;
        },
        update: (data) => data.order.containers,
      },
      originCustomers: {
        query: ReceivingCustomersQuery,
        debounce: 100,
        variables() {
          // Use vue reactive properties here
          return {
            name: (this as any).originCustomerNameQuery,
          };
        },
        update: (data) => data.receivingCustomers,
        skip(): boolean {
          const vm = (this as any);

          return !vm.originCustomerNameQuery;
        },
      },
      destinationCustomers: {
        query: ReceivingCustomersQuery,
        debounce: 100,
        variables() {
          // Use vue reactive properties here
          return {
            name: (this as any).destinationCustomerNameQuery,
          };
        },
        update: (data) => data.receivingCustomers,
        skip(): boolean {
          const vm = (this as any);

          return !vm.originCustomerNameQuery;
        },
      },
      company: {
        query: CompanyQuery,
      },
      vessels: {
        query: VesselsQuery
      },
      drivers: {
        query: DriversQuery,
      },
      chassis: {
        query: ChassisQuery,
      },
    },
  },
} as any);
</script>

<style lang="scss" scoped>
.trip-card {
  border: 1px solid black;
}

.uk-badge {
  // display: inline-block !important;
  text-align: center !important;
  // height: none !important;
}

.clr-storm-6 {
  background-color: #ccdfe5 !important;
}

.tall-row {
  height: 80px;
  background-color: darkcyan !important;
}

.uk-text-black {
  color: black;
}

.display-table {
  display: table;
}
</style>
